<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useInboxConnectionHealth } from 'dashboard/composables/useInboxConnectionHealth';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

/**
 * InboxConnectionHealthNotice
 *
 * Floating card mounted once in App.vue. Watches the inbox store and shows
 * a persistent notice when one or more inboxes are disconnected.
 *
 * Sits at the BOTTOM of the viewport (SiteUp, 2026-08-25): anchored to the top
 * it covered the conversation list's search field and header — the notice is
 * persistent, so it stole that space for as long as an inbox stayed down. On
 * mobile it clears the MobileBottomNav (fixed bottom-0, md:hidden).
 *
 * Mute is persisted per inbox in ui_settings.inbox_health_muted. Closing
 * with X only hides the card for this session. When a muted inbox becomes
 * connected again, its mute key is removed so the next drop can warn.
 *
 * Real WAHA refresh: administrators refresh on mount, account switch and
 * while the tab is visible. A per-account localStorage throttle limits all
 * tabs to one batch every five minutes. The inbox list is fetched first and
 * its returned records are used for the matching account, preventing stale
 * inbox IDs during account switches.
 *
 * CTA:
 * - Single visible disconnected inbox: settings_inbox_show with accountId +
 *   inboxId + tab=configuration via accountScopedRoute.
 * - Multiple visible disconnected inboxes: settings_inbox_list with accountId.
 * - Non-admins get no reconnect action (route requires administrator).
 */

const MUTE_SETTING_KEY = 'inbox_health_muted';
const REFRESH_INTERVAL_MS = 300000;
const REFRESH_STORAGE_KEY = 'waha-health-last-refresh';

const { t } = useI18n();
const { accountId, accountScopedRoute } = useAccount();
const { uiSettings, updateUISettings } = useUISettings();
const {
  disconnectedInboxes,
  connectedInboxes,
  canManageInboxes,
  refreshInboxes,
  refreshWahaStatuses,
} = useInboxConnectionHealth();

const sessionDismissedIds = ref(new Set());

const mutedMap = computed(() => {
  const raw = uiSettings.value?.[MUTE_SETTING_KEY];
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
});

const isMuted = id => {
  const map = mutedMap.value;
  return Boolean(map[id] || map[String(id)]);
};

const isSessionDismissed = id => {
  const key = String(id);
  return sessionDismissedIds.value.has(key);
};

const visibleInboxes = computed(() =>
  disconnectedInboxes.value.filter(
    inbox => !isMuted(inbox.id) && !isSessionDismissed(inbox.id)
  )
);

const isCardVisible = computed(() => visibleInboxes.value.length > 0);

const title = computed(() => {
  const list = visibleInboxes.value;
  if (list.length === 1) {
    return t('INBOX_CONNECTION_HEALTH.TITLE_SINGLE', {
      name: list[0]?.name || '',
    });
  }
  return t('INBOX_CONNECTION_HEALTH.TITLE_MULTIPLE', {
    count: list.length,
  });
});

const body = computed(() =>
  visibleInboxes.value.length > 1
    ? t('INBOX_CONNECTION_HEALTH.BODY_MULTIPLE')
    : t('INBOX_CONNECTION_HEALTH.BODY_SINGLE')
);

const muteLabel = computed(() =>
  visibleInboxes.value.length > 1
    ? t('INBOX_CONNECTION_HEALTH.MUTE_MULTIPLE')
    : t('INBOX_CONNECTION_HEALTH.MUTE_SINGLE')
);

const reconnectTo = computed(() => {
  if (!canManageInboxes.value) return null;

  if (visibleInboxes.value.length === 1) {
    const inbox = visibleInboxes.value[0];
    return accountScopedRoute('settings_inbox_show', {
      inboxId: inbox.id,
      tab: 'configuration',
    });
  }

  if (visibleInboxes.value.length > 1) {
    return accountScopedRoute('settings_inbox_list');
  }

  return null;
});

const muteVisibleInboxes = () => {
  const next = { ...mutedMap.value };
  visibleInboxes.value.forEach(inbox => {
    next[String(inbox.id)] = true;
  });
  updateUISettings({ [MUTE_SETTING_KEY]: next });
};

const dismissForSession = () => {
  sessionDismissedIds.value = new Set([
    ...sessionDismissedIds.value,
    ...visibleInboxes.value.map(inbox => String(inbox.id)),
  ]);
};

const pruneSessionDismissed = currentIds => {
  sessionDismissedIds.value = new Set(
    [...sessionDismissedIds.value].filter(id => currentIds.has(String(id)))
  );
};

const unmuteConnectedInboxes = () => {
  const current = { ...mutedMap.value };
  const connectedIds = new Set(
    connectedInboxes.value.map(inbox => String(inbox.id))
  );
  let changed = false;

  Object.keys(current).forEach(key => {
    if (connectedIds.has(String(key))) {
      delete current[key];
      changed = true;
    }
  });

  if (changed) {
    updateUISettings({ [MUTE_SETTING_KEY]: current });
  }
};

watch(
  () => disconnectedInboxes.value.map(inbox => String(inbox.id)).join(','),
  value => {
    const currentIds = new Set(value.split(',').filter(Boolean));
    pruneSessionDismissed(currentIds);
  }
);

watch(
  () => connectedInboxes.value.map(inbox => String(inbox.id)).join(','),
  () => unmuteConnectedInboxes(),
  { immediate: true }
);

let intervalTimer = null;

const refreshForAccount = async id => {
  if (!canManageInboxes.value || !id) return;

  const storageKey = `${REFRESH_STORAGE_KEY}:${id}`;
  const lastRefreshAt = Number(window.localStorage.getItem(storageKey) || 0);
  if (lastRefreshAt && Date.now() - lastRefreshAt < REFRESH_INTERVAL_MS) return;

  window.localStorage.setItem(storageKey, Date.now().toString());
  const inboxRecords = await refreshInboxes(id);
  if (!inboxRecords || id !== accountId.value) return;

  await refreshWahaStatuses(id, inboxRecords);
};

watch(accountId, id => {
  sessionDismissedIds.value = new Set();
  refreshForAccount(id);
});

const stopInterval = () => {
  if (intervalTimer) {
    clearInterval(intervalTimer);
    intervalTimer = null;
  }
};

const startInterval = () => {
  stopInterval();
  intervalTimer = setInterval(() => {
    if (document.visibilityState === 'visible') {
      refreshForAccount(accountId.value);
    }
  }, REFRESH_INTERVAL_MS);
};

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    refreshForAccount(accountId.value);
  }
};

onMounted(() => {
  document.addEventListener('visibilitychange', handleVisibilityChange);
  refreshForAccount(accountId.value);
  startInterval();
});

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  stopInterval();
  sessionDismissedIds.value = new Set();
});
</script>

<template>
  <TeleportWithDirection to="body">
    <div
      v-if="isCardVisible"
      role="status"
      aria-live="polite"
      :aria-label="title"
      class="fixed bottom-24 start-1/2 z-[70] w-[calc(100%-2rem)] max-w-[28rem] -translate-x-1/2 animate-fade-in-up rounded-2xl border border-n-weak bg-n-solid-2 p-4 shadow-xl md:bottom-4"
      data-testid="inbox-health-notice"
    >
      <div class="flex items-start gap-3">
        <span
          class="flex size-9 shrink-0 items-center justify-center rounded-full bg-n-ruby-3 text-n-ruby-11"
          aria-hidden="true"
        >
          <span class="i-ri-wifi-off-line size-5" />
        </span>
        <div class="min-w-0 flex-1">
          <p class="m-0 text-sm font-semibold text-n-slate-12">
            {{ title }}
          </p>
          <p class="m-0 mt-1 text-sm leading-relaxed text-n-slate-11">
            {{ body }}
          </p>
        </div>
        <button
          type="button"
          class="inline-flex size-9 shrink-0 items-center justify-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
          data-testid="inbox-health-notice-close"
          :aria-label="t('INBOX_CONNECTION_HEALTH.CLOSE')"
          @click="dismissForSession"
        >
          <span class="i-ri-close-line size-5" aria-hidden="true" />
        </button>
      </div>
      <div class="mt-3 flex flex-wrap items-center gap-2">
        <router-link
          v-if="reconnectTo"
          :to="reconnectTo"
          class="inline-flex min-h-9 items-center justify-center rounded-full bg-n-slate-12 px-4 text-sm font-semibold leading-snug text-n-background hover:opacity-90"
          data-testid="inbox-health-notice-reconnect"
        >
          {{ t('INBOX_CONNECTION_HEALTH.ACTION_RECONNECT') }}
        </router-link>
        <button
          type="button"
          class="inline-flex min-h-9 items-center justify-center rounded-full px-3 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
          data-testid="inbox-health-notice-mute"
          @click="muteVisibleInboxes"
        >
          {{ muteLabel }}
        </button>
      </div>
    </div>
  </TeleportWithDirection>
</template>
